/**
 * proposalActions.js - what Confirm does with an assistant proposal
 * (server/lib/chatActions.js plans it; ProposalCard.jsx shows it).
 *
 * The server never writes a proposal. Each action carries `exec`, the request
 * the screen sends on Confirm - through the SAME endpoints the app's own
 * screens use, with the signed-in person's token (the window.fetch
 * interceptor in src/index.js), so the area gate, validation, the audit log
 * (marked `_via: 'assistant'`, shown as "(via assistant)" in the Activity
 * log), live updates and notifications apply exactly as for a manual edit.
 *
 *   project.create / project.save   AppContext createProject / saveProject (the
 *                                   editor's saves: quote_taken and, with
 *                                   `_expect`, edit_conflict come back as 409s)
 *   task.create / task.update       /api/crm-actions
 *   comment.add                     /api/projects/:id/comments
 *   activity.add                    /api/accounts/:id/activity
 *   account.create / account.update /api/accounts
 *   contact.create / contact.update /api/accounts/:id/contacts
 *
 * Only these ops exist here: a proposal naming anything else does nothing.
 */
export const VIA = { _via: 'assistant' };
const enc = encodeURIComponent;

/** The app's deep link to a project row (as the server's projectHref). */
export const projectHref = (row) => {
  const won = ['won', 'complete'].includes(
    String((row && row.status) || '')
      .trim()
      .toLowerCase()
  );
  return `#/${won ? 'projects' : 'opportunities'}?project=${enc(row.id)}`;
};

/** One JSON request: { ok, status, body }. */
export async function sendJson(path, method, body) {
  const res = await fetch(path, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const parsed = await res.json().catch(() => null);
  return { ok: res.ok, status: res.status, body: parsed };
}

/** A save's answer as a result line: { ok, status, code, error, body, href }. */
function resultOf(out, href) {
  const body = out && out.body;
  if (out && out.ok) return { ok: true, status: out.status, body, href: href || null };
  const status = out ? out.status : 0;
  let error =
    (body && body.error) ||
    (status === 403
      ? 'Your access does not allow this change.'
      : status
        ? `The server refused it (${status}).`
        : 'Could not reach the server.');
  if (status === 404 && !(body && body.error)) error = 'That record no longer exists.';
  return { ok: false, status, code: (body && body.code) || null, error, body };
}

/**
 * Send one proposed action. `api` = { createProject, saveProject, request,
 * refresh } (ChatWidget hands in AppContext's and sendJson). `opts` are the
 * person's answers to a refusal: allowDuplicateQuote / quote (the 409
 * quote_taken choices, as the editor offers them) and force (save over an
 * edit_conflict).
 */
export async function runAction(action, api, opts = {}) {
  const x = (action && action.exec) || {};
  const target = (action && action.target) || {};
  const request = api.request || sendJson;
  switch (x.op) {
    case 'project.create': {
      const body = { ...x.body, ...VIA };
      if (opts.quote) body.quote_letter = opts.quote;
      const allowDuplicateQuote = opts.allowDuplicateQuote !== undefined ? opts.allowDuplicateQuote : !!x.allowDuplicateQuote;
      const out = await api.createProject(body, { allowDuplicateQuote });
      return resultOf(out, out && out.ok && out.body ? projectHref(out.body) : null);
    }
    case 'project.save': {
      const out = await api.saveProject(x.id, { ...x.changes, ...VIA }, { expect: x.expect, force: !!opts.force });
      return resultOf(out, out && out.ok && out.body ? projectHref(out.body) : target.href);
    }
    case 'task.create':
      return resultOf(await request('/api/crm-actions', 'POST', { ...x.body, ...VIA }), target.href);
    case 'task.update':
      return resultOf(await request(`/api/crm-actions/${enc(x.id)}`, 'PUT', { ...x.body, ...VIA }), target.href);
    case 'comment.add':
      return resultOf(await request(`/api/projects/${enc(x.projectId)}/comments`, 'POST', { ...x.body, ...VIA }), target.href);
    case 'activity.add':
      return refreshed(
        api,
        resultOf(await request(`/api/accounts/${enc(x.accountId)}/activity`, 'POST', { ...x.body, ...VIA }), target.href)
      );
    case 'account.create': {
      const out = await request('/api/accounts', 'POST', { ...x.body, ...VIA });
      return refreshed(api, resultOf(out, out.ok && out.body ? `#/crm?account=${enc(out.body.id)}` : null));
    }
    case 'account.update':
      return refreshed(api, resultOf(await request(`/api/accounts/${enc(x.id)}`, 'PUT', { ...x.body, ...VIA }), target.href));
    case 'contact.create': {
      const out = await request(`/api/accounts/${enc(x.accountId)}/contacts`, 'POST', { ...x.body, ...VIA });
      return refreshed(api, resultOf(out, out.ok && out.body ? `#/crm?contact=${enc(out.body.id)}` : target.href));
    }
    case 'contact.update':
      return refreshed(
        api,
        resultOf(await request(`/api/accounts/${enc(x.accountId)}/contacts/${enc(x.contactId)}`, 'PUT', { ...x.body, ...VIA }), target.href)
      );
    default:
      return { ok: false, status: 0, code: null, error: 'The assistant proposed something this screen cannot do.' };
  }
}

/** The accounts list re-reads after an account / contact change (as updateAccount does). */
function refreshed(api, result) {
  if (result.ok && api.refresh) api.refresh(['accounts']);
  return result;
}

/** What a result says, in a line (for the card and the model). */
export function resultLine(action, result) {
  if (!result) return `${action.title}: not run`;
  if (result.ok) {
    const id = result.body && result.body.id ? ` (id ${result.body.id})` : '';
    return `${action.title}: done${id}`;
  }
  return `${action.title}: FAILED - ${result.error}${result.code ? ` [${result.code}]` : ''}`;
}

/** The <app_outcome> text the model reads next turn. */
export function outcomeText(proposal, results, { cancelled = false } = {}) {
  if (cancelled) return `The user cancelled proposal ${proposal.id}. Nothing was changed.`;
  const lines = proposal.actions.map((a) => {
    const r = results[a.key];
    if (!r) return `- ${a.title}: not selected, not changed`;
    return `- ${resultLine(a, r)}`;
  });
  return `Outcome of proposal ${proposal.id} (the user pressed Confirm):\n${lines.join('\n')}`;
}

/** The quiet line the chat shows for an outcome message (the model reads the whole text). */
export function outcomeLabel(text) {
  const t = String(text || '');
  if (/cancelled proposal/.test(t)) return 'Cancelled - nothing was changed.';
  const lines = t.split('\n').filter((l) => l.startsWith('- '));
  const done = lines.filter((l) => /: done( \(|$)/.test(l)).length;
  const failed = lines.filter((l) => /: FAILED - /.test(l)).length;
  const parts = [];
  if (done) parts.push(`${done} done`);
  if (failed) parts.push(`${failed} failed`);
  return `${/^Follow-up/.test(t) ? 'Retried' : 'Confirmed'}: ${parts.join(', ') || 'nothing sent'}. The assistant can see this.`;
}
